import { diningOptionsByPark } from './diningOptions.js';

// Category labels from sections.js (exact string match for restaurantStrategy) — exported for tests
export const DINING_CATEGORY_LABELS = {
  CHARACTER_DINING: "Character Dining – Meals with character meet-and-greets",
  SIGNATURE_DINING: "Signature Dining – Upscale / \"Special Night\" Restaurants",
  THEMED_DINING: "Themed Dining – Immersive restaurants for atmosphere and fun",
  QUICK_SERVICE: "Quick Service – Counter service, mobile order, fast-casual",
  SEE_IT_ALL: "See it All – No preference, show me everything",
};

const SEE_IT_ALL = DINING_CATEGORY_LABELS.SEE_IT_ALL;
const NO_PREFERENCE_LEGACY = "No Preference – Let the agent choose";
const CHARACTER_DINING = DINING_CATEGORY_LABELS.CHARACTER_DINING;
const SIGNATURE_DINING = DINING_CATEGORY_LABELS.SIGNATURE_DINING;
const THEMED_DINING = DINING_CATEGORY_LABELS.THEMED_DINING;
const QUICK_SERVICE = DINING_CATEGORY_LABELS.QUICK_SERVICE;

// Build restaurantOptionsByPark directly from diningOptionsByPark (canonical meal-only; no inference)
export const restaurantOptionsByPark = {};

Object.entries(diningOptionsByPark).forEach(([parkName, restaurants]) => {
  let mappedParkName = parkName;
  if (parkName === "Disney's Hollywood Studios") {
    mappedParkName = "Hollywood Studios";
  } else if (parkName === "Disney's Animal Kingdom Theme Park") {
    mappedParkName = "Animal Kingdom";
  }

  restaurantOptionsByPark[mappedParkName] = restaurants.map((r) => ({
    name: r.name,
    service: r.service,
    category: r.category,
    cuisine: r.cuisine,
    price: r.price,
    location: mappedParkName,
    tags: r.tags,
    warningNote: r.warningNote,
    // Display label for service (UI only)
    serviceLabel: r.service === 'QS' ? 'Quick Service' : 'Table Service',
    // Cuisine/price helpers for cuisine filter (unchanged)
    isAmerican: r.cuisine?.includes("American"),
    isItalian: r.cuisine?.includes("Italian"),
    isMexican: r.cuisine?.includes("Mexican"),
    isAsian: r.cuisine?.includes("Asian") || r.cuisine?.includes("Chinese") || r.cuisine?.includes("Japanese") || r.cuisine?.includes("Sushi"),
    isFrench: r.cuisine?.includes("French"),
    isSeafood: r.cuisine?.includes("Seafood"),
    isSteakhouse: r.cuisine?.includes("Steakhouse"),
    isBBQ: r.cuisine?.includes("BBQ"),
    isPlantBased: r.tags?.includes("Plant-Based") || r.cuisine?.includes("Plant-Based"),
    isBudget: r.price === "$",
    isModerate: r.price === "$$",
    isExpensive: r.price === "$$$",
    isLuxury: r.price === "$$$$",
  }));
});

export const getRestaurantsByPark = (parkName, filters = {}) => {
  const restaurants = restaurantOptionsByPark[parkName] || [];
  if (!filters || Object.keys(filters).length === 0) return restaurants;
  return restaurants.filter((restaurant) => {
    return Object.entries(filters).every(([key, value]) => {
      if (Array.isArray(value)) return value.some((v) => restaurant[key] === v);
      return restaurant[key] === value;
    });
  });
};

export const getAllRestaurantsByParks = (selectedParks, filters = {}) => {
  const allRestaurants = [];
  selectedParks.forEach((parkName) => {
    allRestaurants.push(...getRestaurantsByPark(parkName, filters));
  });
  return allRestaurants;
};

// Normalize category label so form values (e.g. curly quotes " ") match data (straight ")
function normalizeCategoryLabel(s) {
  if (typeof s !== "string") return s;
  return s.replace(/\u201C|\u201D/g, '"');
}

// Filter by explicit category (no inference)
export const getRestaurantsByDiningPreferences = (selectedParks, diningPreferences = []) => {
  if (
    diningPreferences.length === 0 ||
    diningPreferences.includes(SEE_IT_ALL) ||
    diningPreferences.includes(NO_PREFERENCE_LEGACY)
  ) {
    return getAllRestaurantsByParks(selectedParks, {});
  }

  const allRestaurants = getAllRestaurantsByParks(selectedParks, {});
  return allRestaurants.filter((restaurant) => {
    return diningPreferences.some((pref) => {
      const normalized = normalizeCategoryLabel(pref);
      switch (normalized) {
        case CHARACTER_DINING:
          return restaurant.category === CHARACTER_DINING;
        case SIGNATURE_DINING:
          return restaurant.category === SIGNATURE_DINING;
        case THEMED_DINING:
          return restaurant.category === THEMED_DINING;
        case QUICK_SERVICE:
          return restaurant.category === QUICK_SERVICE;
        case SEE_IT_ALL:
        case NO_PREFERENCE_LEGACY:
          return true;
        default:
          return false;
      }
    });
  });
};

export const getRestaurantsByCuisinePreferences = (selectedParks, cuisinePreferences = []) => {
  if (
    cuisinePreferences.length === 0 ||
    cuisinePreferences.includes("No Preference – Let the agent choose")
  ) {
    return getAllRestaurantsByParks(selectedParks, {});
  }

  const allRestaurants = getAllRestaurantsByParks(selectedParks, {});
  return allRestaurants.filter((restaurant) => {
    return cuisinePreferences.some((pref) => {
      switch (pref) {
        case "American":
          return restaurant.isAmerican;
        case "Italian":
          return restaurant.isItalian;
        case "Mexican":
          return restaurant.isMexican;
        case "Asian":
          return restaurant.isAsian;
        case "French":
          return restaurant.isFrench;
        case "Seafood":
          return restaurant.isSeafood;
        case "Steakhouse":
          return restaurant.isSteakhouse;
        case "BBQ":
          return restaurant.isBBQ;
        case "Plant-Based":
          return restaurant.isPlantBased;
        default:
          return false;
      }
    });
  });
};